import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, inject, signal, computed, HostListener, viewChild, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { SystemHealthService } from '@services/system-health.service';
import { HealthCheckResult, Module, Model, LocalizationMap } from '@models/system-health.models';
import { VersionInfoComponent } from '@components/version-info/version-info.component';

interface ModuleCard {
  module: Module;
  status: 'healthy' | 'warning' | 'error';
  totalErrors: number;
  moduleErrors: string[];
  modelErrors: Array<{ model: Model; errors: string[]; displayName: string }>;
  displayName: string;
}

@Component({
  selector: 'app-system-status-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    CardModule,
    TagModule,
    ProgressBarModule,
    TableModule,
    TooltipModule,
    VersionInfoComponent
  ],
  templateUrl: './system-status-page.html',
  styleUrls: ['./system-status-page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SystemStatusPageComponent implements OnInit {
  @ViewChild('versionInfoModal') versionInfoModal!: VersionInfoComponent;
  loading = signal(true);
  error = signal<string | null>(null);
  healthData = signal<HealthCheckResult[]>([]);
  moduleCards = signal<ModuleCard[]>([]);
  
  selectedModule = signal<ModuleCard | null>(null);
  showDetailsModal = false;
  isVersionModalOpen = signal(false);
    
  private systemHealthService = inject(SystemHealthService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  
  // Computed values
  hasData = computed(() => !this.loading() && !this.error() && this.moduleCards().length > 0);
  
  ngOnInit(): void {
    this.loadSystemStatus();
  }
  
  private loadSystemStatus(): void {
    this.loading.set(true);
    this.error.set(null);
    
    this.systemHealthService.getHealthCheck().subscribe({
      next: (data) => {
        this.healthData.set(data);
        this.processModuleData(data);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading system status:', err);
        this.error.set('Failed to load system status');
        this.loading.set(false);
      }
    });
  }
  
  private processModuleData(data: HealthCheckResult[]): void {
    if (data.length === 0) {
      this.moduleCards.set([]);
      return;
    }
    
    const sortedData = [...data].sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
    const latest = sortedData[0];
    
    const cards = latest.modules.map((rawModule) => {
      const module = this.normalizeModule(rawModule);
      const moduleErrors = this.getLocalizedErrors(module.localizedErrors, module.errors);
      const modelErrors = module.models.map((model: Model) => ({
        model,
        displayName: this.getModelName(model),
        errors: this.getLocalizedErrors(model.localizedErrors, model.errors)
      }));
      
      const totalErrors = moduleErrors.length + modelErrors.reduce((sum, item) => sum + item.errors.length, 0);
      
      let status: 'healthy' | 'warning' | 'error' = 'healthy';
      if (totalErrors > 0) {
        status = 'error';
      }

      return {
        module,
        status,
        totalErrors,
        moduleErrors,
        modelErrors,
        displayName: this.getModuleName(module)
      };
    }).sort((a, b) => {
      if (a.status === 'error' && b.status !== 'error') return -1;
      if (a.status !== 'error' && b.status === 'error') return 1;
      if (a.status === 'error' && b.status === 'error') {
        return b.totalErrors - a.totalErrors;
      }
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
    
    this.moduleCards.set(cards);
  }
  
  getStatusClass(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return 'status-healthy';
      case 'warning': return 'status-warning';
      case 'error': return 'status-error';
      default: return 'status-unknown';
    }
  }
  
  getStatusText(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return this.translateService.instant('Healthy');
      case 'warning': return this.translateService.instant('Warning');
      case 'error': return this.translateService.instant('Error');
      default: return this.translateService.instant('Unknown');
    }
  }
  
  getStatusIcon(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return '../../../assets/icons/main/light/check-circle.svg';
      case 'warning': return '../../../assets/icons/main/light/alert-line.svg';
      case 'error': return '../../../assets/icons/main/light/alert-fill.svg';
      default: return '../../../assets/icons/main/light/alert-line.svg';
    }
  }
  
  getProgressBarColor(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  }
  
  showModuleDetails(moduleCard: ModuleCard): void {
    this.selectedModule.set(moduleCard);
    this.showDetailsModal = true;
  }
  
  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedModule.set(null);
  }
  
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showDetailsModal) {
      this.closeDetailsModal();
    }
    if (this.isVersionModalOpen()) {
      this.closeVersionModal();
    }
  }
  
  refreshData(): void {
    this.loadSystemStatus();
  }
  
  getModuleName(module: Module): string {
    return this.resolveLocalizedName(this.getModuleLocalization(module), this.getModuleFallbackName(module));
  }
  
  getModelName(model: Model): string {
    return model.modelName || 'Unknown Model';
  }
  
  getModelCount(module: Module): number {
    return module.models ? module.models.length : 0;
  }
  
  getHealthyModelCount(module: Module): number {
    if (!module.models) return 0;
    return module.models.filter(model => (model.errors || []).length === 0).length;
  }
  
  getErrorModelCount(module: Module): number {
    if (!module.models) return 0;
    return module.models.filter(model => (model.errors || []).length > 0).length;
  }
  
  getHealthPercentage(module: Module): number {
    const total = this.getModelCount(module);
    if (total === 0) return 100;
    return Math.round((this.getHealthyModelCount(module) / total) * 100);
  }
  
  getErrorPercentage(module: Module): number {
    const total = this.getModelCount(module);
    if (total === 0) return 0;
    return Math.round((this.getErrorModelCount(module) / total) * 100);
  }
  
  truncateText(text: string, maxLength: number = 30): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Открыть модальное окно с версией
   */
  openVersionModal(): void {    
    if (!this.isVersionModalOpen() && this.versionInfoModal) {
      this.isVersionModalOpen.set(true);
      this.versionInfoModal.open();
    }
  }
  
  /**
   * Закрыть модальное окно с версией
   */
  closeVersionModal(): void {
    if (this.isVersionModalOpen() && this.versionInfoModal) {
      this.isVersionModalOpen.set(false);
      this.versionInfoModal.close();
    }
  }
  
  private normalizeModule(rawModule: any): Module {
    const moduleNames = this.getModuleLocalization(rawModule) || undefined;
    const localizedErrors = this.getLocalizedErrorsArray(rawModule) || undefined;
    const errors = this.coerceErrors(rawModule?.errors ?? rawModule?.Errors);
    const rawModels = rawModule?.mоdels ?? rawModule?.models ?? rawModule?.Models ?? [];
    const models = rawModels.map((model: any) => this.normalizeModel(model));
    
    return {
      moduleName: this.getModuleFallbackName(rawModule),
      moduleNames,
      localizedErrors,
      models,
      errors
    };
  }
  
  private normalizeModel(rawModel: any): Model {
    const fallbackName = this.getModelFallbackName(rawModel);
    
    return {
      modelName: fallbackName,
      modelNames: undefined,
      localizedErrors: this.getLocalizedErrorsArray(rawModel) || undefined,
      dbLayerCheck: rawModel?.dbLayerCheck ?? rawModel?.DbLayerCheck ?? false,
      logicLayerCheck: rawModel?.logicLayerCheck ?? rawModel?.LogicLayerCheck ?? false,
      webApi: rawModel?.webApi ?? rawModel?.WebApi ?? false,
      errors: this.coerceErrors(rawModel?.errors ?? rawModel?.Errors)
    };
  }
  
  private getModuleLocalization(module: any): LocalizationMap | null {
    return this.toLocalizationMap(module?.moduleNames ?? module?.ModuleNames);
  }
  
  private getModuleFallbackName(module: any): string {
    const raw = module?.moduleName || module?.ModuleName || '';
    if (!raw) return 'Unknown Module';
    return this.humanizeName(raw);
  }
  
  private getModelFallbackName(model: any): string {
    const raw = model?.modelName || model?.ModelName || '';
    if (!raw) return 'Unknown Model';
    return this.humanizeName(raw);
  }
  
  private getLocalizedErrorsArray(source: any): LocalizationMap[] {
    const value = source?.localizedErrors ?? source?.LocalizedErrors;
    if (!value) {
      return [];
    }
    
    if (Array.isArray(value)) {
      return value
        .map(item => this.toLocalizationMap(item))
        .filter((item): item is LocalizationMap => !!item);
    }
    
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map(item => this.toLocalizationMap(item))
            .filter((item): item is LocalizationMap => !!item);
        }
        
        const single = this.toLocalizationMap(parsed);
        return single ? [single] : [];
      } catch {
        return [];
      }
    }
    
    const single = this.toLocalizationMap(value);
    return single ? [single] : [];
  }
  
  private resolveLocalizedName(localization: LocalizationMap | null | undefined, fallback: string): string {
    if (!localization) return fallback;
    
    const candidates = this.getLanguageCandidates();
    
    for (const lang of candidates) {
      const value = this.findLocalizationValue(localization, lang);
      if (value) return value;
    }
    
    return fallback;
  }
  
  private findLocalizationValue(localization: LocalizationMap, key: string): string | undefined {
    return localization[key];
  }
  
  private getLanguageCandidates(): string[] {
    const currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'en';
    return [currentLang, currentLang.split('-')[0], 'en'];
  }
  
  private humanizeName(value: string): string {
    if (!value) return '';
    return value
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }
  
  private toLocalizationMap(value: any): LocalizationMap | null {
    if (!value) return null;
    
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value as LocalizationMap;
    }
    
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as LocalizationMap;
        }
      } catch {
        // Невалидный JSON
      }
    }
    
    return null;
  }
  
  private getLocalizedErrors(localizedErrors: LocalizationMap[] | undefined, fallbackErrors: string[] = []): string[] {
    if (localizedErrors && localizedErrors.length > 0) {
      return localizedErrors.map((map, index) => {
        const fallback = fallbackErrors[index] ?? '';
        return this.resolveLocalizedName(map, fallback || this.translateService.instant('Unknown'));
      }).filter(msg => !!msg);
    }
    
    return fallbackErrors;
  }
  
  private coerceErrors(value: any): string[] {
    if (!value) {
      return [];
    }
    
    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }
    
    return [String(value)];
  }
}